import React, { useMemo, useState } from "react";
import { Button } from "../ui/button";

const VIEWS = [
  { key: "review", label: "Review Queue" },
  { key: "overview", label: "Uebersicht" },
  { key: "generate", label: "Generierung" },
  { key: "batches", label: "Batches" },
  { key: "attempts", label: "Attempts" },
];

const MODULE_LABELS = {
  reading: "Lesen",
  listening: "Hoeren",
  language_elements: "Sprachbausteine",
  writing: "Schreiben",
  speaking: "Sprechen",
};

const STATUS_LABELS = {
  review_pending: "Pruefung offen",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
  archived: "Archiviert",
  completed: "Abgeschlossen",
  draft: "Entwurf",
  reviewed: "Geprueft",
};

function formatLabel(value, fallback = "Nicht gesetzt") {
  if (!value) return fallback;
  return String(value).replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "Noch keine Aktivitaet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Noch keine Aktivitaet";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function formatModuleLabel(moduleKey) {
  return MODULE_LABELS[moduleKey] || formatLabel(moduleKey);
}

function formatStatusLabel(status) {
  return STATUS_LABELS[status] || formatLabel(status);
}

function truncateText(value, maxLength = 180) {
  const text = String(value || "").trim();
  if (!text) return "Kein Text vorhanden.";
  return text.length <= maxLength ? text : `${text.slice(0, maxLength).trim()}...`;
}

function poolPreview(item) {
  const content = item?.content || {};
  if (item?.module_key === "listening") return content.transcript || content.prompt || "Kein Vorschautext vorhanden.";
  if (item?.module_key === "speaking") return Array.isArray(content.prompts) ? content.prompts.join(" | ") : "Kein Promptset vorhanden.";
  return content.prompt || "Kein Prompt vorhanden.";
}

export default function AdminB1Workspace({
  languageHealth,
  languagePool,
  languageBatches,
  languageAttempts,
  languageOpsState,
  generatorForm,
  setGeneratorForm,
  handleGenerateBatch,
  handleReviewItem,
}) {
  const [view, setView] = useState("review");

  const summary = useMemo(() => ({
    pending: languageHealth.summary?.pending_count ?? languagePool.length,
    approved: languageHealth.summary?.approved_count ?? 0,
    rejected: languageHealth.summary?.rejected_count ?? 0,
    fallbackModules: Array.isArray(languageHealth.summary?.fallback_needed_modules)
      ? languageHealth.summary.fallback_needed_modules
      : [],
  }), [languageHealth, languagePool.length]);

  const readingModuleHealth = languageHealth.modules.find((module) => module.module_key === "reading") || null;

  return (
    <div className="dashboard-local-workspace">
      {languageOpsState.error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{languageOpsState.error}</div> : null}

      <div className="dashboard-local-nav">
        {VIEWS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`dashboard-local-nav__item${view === item.key ? " is-active" : ""}`}
            onClick={() => setView(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {view === "review" ? (
        <div className="dashboard-surface-grid dashboard-surface-grid-admin">
          <div className="dashboard-surface dashboard-surface-strong">
            <span className="dashboard-summary-label">Offene Review Queue</span>
            <div className="dashboard-review-list">
              {languageOpsState.loading ? <div className="dashboard-empty-tile">B1-Pool wird geladen...</div> : null}
              {!languageOpsState.loading && languagePool.length === 0 ? <div className="dashboard-empty-tile">Aktuell liegen keine offenen B1-Pool-Items zur Pruefung vor.</div> : null}
              {!languageOpsState.loading && languagePool.map((item) => {
                const content = item?.content || {};
                const flags = Array.isArray(item?.review?.guardrail_flags) ? item.review.guardrail_flags : [];
                return (
                  <article key={item.id} className="dashboard-review-card">
                    <div className="dashboard-review-card__head">
                      <div>
                        <strong>Item #{item.id}</strong>
                        <p>{formatModuleLabel(item.module_key)} · Batch {item.generation_batch_id || "Seed"} · {formatStatusLabel(item.status)}</p>
                      </div>
                      <span className="dashboard-pill dashboard-pill-soft">Q {item.review?.quality_score ?? "-"}</span>
                    </div>
                    <div className="dashboard-review-card__meta">
                      <span>Textsorte {formatLabel(content.text_type)}</span>
                      <span>Fragetyp {formatLabel(content.question_type)}</span>
                      <span>Schwierigkeit {formatLabel(content.difficulty_band)}</span>
                      <span>Zuletzt {formatDateTime(item.last_used_at)}</span>
                    </div>
                    {flags.length ? <div className="dashboard-flag-row">{flags.map((flag) => <span key={flag} className="dashboard-pill">{formatLabel(flag)}</span>)}</div> : null}
                    <p className="dashboard-review-card__copy">{poolPreview(item)}</p>
                    <div className="dashboard-review-card__actions">
                      <Button className="dashboard-action-button dashboard-action-button-success" onClick={() => handleReviewItem(item.id, "approve")} disabled={languageOpsState.busy === `review:${item.id}`}>Freigeben</Button>
                      <Button variant="outline" className="dashboard-action-button dashboard-action-button-danger" onClick={() => handleReviewItem(item.id, "reject")} disabled={languageOpsState.busy === `review:${item.id}`}>Ablehnen</Button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="dashboard-secondary-rail dashboard-secondary-rail-inline">
            <section className="dashboard-rail-card">
              <div className="dashboard-rail-metrics">
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Review offen</span><strong>{summary.pending}</strong></article>
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Freigegeben</span><strong>{summary.approved}</strong></article>
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Abgelehnt</span><strong>{summary.rejected}</strong></article>
              </div>
            </section>

            <section className="dashboard-rail-card">
              <div className="dashboard-note">
                <p>Reading-Guardrails bleiben direkt im Review-Pfad sichtbar, nicht in einer zweiten Hero-Ebene.</p>
              </div>
              <div className="dashboard-rail-metrics">
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Textsorten</span><strong>{readingModuleHealth?.reading_text_type_count ?? 0}</strong></article>
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Fragetypen</span><strong>{readingModuleHealth?.reading_question_type_count ?? 0}</strong></article>
                <article className="dashboard-metric-card"><span className="dashboard-summary-label">Kognitive Muster</span><strong>{readingModuleHealth?.reading_cognitive_operation_count ?? 0}</strong></article>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {view === "overview" ? (
        <div className="dashboard-surface-grid">
          {languageHealth.modules.map((module) => (
            <article key={module.module_key} className="dashboard-surface">
              <span className="dashboard-summary-label">{formatModuleLabel(module.module_key)}</span>
              <strong>{module.ready ? "Bereit" : "Fallback aktiv"}</strong>
              <p>Freigegeben {module.approved_count} · Offen {module.pending_count} · Weak {module.weak_items}</p>
            </article>
          ))}
        </div>
      ) : null}

      {view === "generate" ? (
        <div className="dashboard-surface-grid dashboard-surface-grid-slim">
          <section className="dashboard-surface">
            <span className="dashboard-summary-label">Neuen Batch starten</span>
            <div className="dashboard-form-grid">
              <select value={generatorForm.module_key} onChange={(event) => setGeneratorForm((current) => ({ ...current, module_key: event.target.value }))} className="dashboard-field">
                <option value="reading">Lesen</option>
                <option value="listening">Hoeren</option>
                <option value="language_elements">Sprachbausteine</option>
                <option value="writing">Schreiben</option>
                <option value="speaking">Sprechen</option>
              </select>
              <input type="number" min="1" max="20" value={generatorForm.count} onChange={(event) => setGeneratorForm((current) => ({ ...current, count: Number(event.target.value) || 1 }))} className="dashboard-field" />
              <input type="text" value={generatorForm.focus_text} onChange={(event) => setGeneratorForm((current) => ({ ...current, focus_text: event.target.value }))} placeholder="Alltag, Termine, Reisen ..." className="dashboard-field dashboard-field-wide" />
              <Button className="dashboard-action-button" onClick={handleGenerateBatch} disabled={languageOpsState.busy === "generate"}>{languageOpsState.busy === "generate" ? "Erzeuge..." : "Batch starten"}</Button>
            </div>
          </section>

          <section className="dashboard-surface">
            <span className="dashboard-summary-label">Guardrails</span>
            <ul className="dashboard-bullet-list">
              <li>Jeder Batch geht zuerst in die Review Queue.</li>
              <li>Kein direkter Candidate-Zugriff auf ungepruefte Items.</li>
              <li>Fachdomaeinen-Leaks und Dubletten bleiben blockiert.</li>
            </ul>
          </section>
        </div>
      ) : null}

      {view === "batches" ? (
        <div className="dashboard-review-list">
          {languageBatches.length ? languageBatches.map((batch) => (
            <article key={batch.id} className="dashboard-review-card">
              <div className="dashboard-review-card__head">
                <div>
                  <strong>Batch #{batch.id}</strong>
                  <p>{formatModuleLabel(batch.module_key)} · {formatStatusLabel(batch.status)}</p>
                </div>
                <span className="dashboard-pill dashboard-pill-soft">Anfrage {batch.requested_count}</span>
              </div>
              <p>Erzeugt {batch.summary?.generated_count ?? 0} · Review offen {batch.summary?.review_pending_count ?? 0} · Abgelehnt {batch.summary?.rejected_count ?? 0}</p>
            </article>
          )) : <div className="dashboard-empty-tile">Noch keine B1-Batches im geladenen Zeitraum.</div>}
        </div>
      ) : null}

      {view === "attempts" ? (
        <div className="dashboard-review-list">
          {languageAttempts.length ? languageAttempts.map((attempt) => (
            <article key={attempt.id} className="dashboard-review-card">
              <div className="dashboard-review-card__head">
                <div>
                  <strong>Attempt #{attempt.id}</strong>
                  <p>User {attempt.userid} · {formatLabel(attempt.test_type)} · {formatLabel(attempt.review_status)}</p>
                </div>
                <span className="dashboard-pill dashboard-pill-soft">{formatStatusLabel(attempt.status)}</span>
              </div>
              <div className="dashboard-review-card__meta">
                <span>Band {attempt.estimated_cefr_band || "-"}</span>
                <span>Gesamt {attempt.overall_language_score ?? "-"}</span>
                <span>Audio {attempt.speaking_audio?.uploaded ? "ja" : "nein"}</span>
                <span>Zuletzt {formatDateTime(attempt.updated_at || attempt.created_at)}</span>
              </div>
              <p className="dashboard-review-card__copy">{truncateText(attempt.speaking_transcript_text)}</p>
            </article>
          )) : <div className="dashboard-empty-tile">Noch keine B1-Versuche im geladenen Zeitraum.</div>}
        </div>
      ) : null}
    </div>
  );
}
